import React, { forwardRef, memo, useCallback, useEffect, useRef, } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { NavigationDrawerGroupTypes, } from '@polestar/component-warehouse-react/components/navigation-drawer/NavigationDrawer';
import Icon from '@polestar/component-warehouse-react/components/icon/Icon';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import Link, { LinkVariants } from '@polestar/component-warehouse-react/components/link/Link';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import { useFocusGroup, useFocusInitial, useFocusRestore, } from '@polestar/component-warehouse-react/hooks/focus';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { flattenGroupsIntoLinks } from '../NavigationDrawer.helpers';
import FontSizes from '@polestar/component-warehouse-react/tokens/font-sizes/FontSizes';
import LineHeights from '@polestar/component-warehouse-react/tokens/line-heights/LineHeights';
import LetterSpacings from '@polestar/component-warehouse-react/tokens/letter-spacings/LetterSpacings';
import useTrackingData, { formatTrackingLabel, } from '@polestar/component-warehouse-react/hooks/useTrackingData';
import { DataLayerAction, DataLayerCategory, } from '@polestar/component-warehouse-react/tokens/analytics/Analytics';
import warnOnce from '@polestar/component-warehouse-react/helpers/warnOnce';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
const NavigationDrawerList = memo(({ links, setColumn, index, activePaths, onKeyDown, active, onFocusChange, ...props }) => {
    const ref = useRef(null);
    const hasLinks = !!(links === null || links === void 0 ? void 0 : links.length);
    const { isKeyboardControlled } = useInteractionMode();
    const { focusIndex, createFocusProps } = useFocusGroup({
        ref,
        initialFocusIndex: 0,
    });
    useFocusRestore(hasLinks && isKeyboardControlled);
    useFocusInitial(ref, hasLinks && isKeyboardControlled && active);
    useEffect(() => {
        if (hasLinks && isKeyboardControlled) {
            onFocusChange(focusIndex);
        }
    }, [isKeyboardControlled, onFocusChange, focusIndex, hasLinks]);
    if (!hasLinks) {
        return null;
    }
    let groupIndex = -1;
    return (React.createElement(NavigationWrapper, { ...props, onKeyDown: onKeyDown, ref: ref, "data-type": 'navigation-list' },
        React.createElement("ul", { ...aria({ role: 'menu' }) }, links.map((link, position) => {
            if (link.groupType === 'large' && (!link.groups || link.groups.length === 0)) {
                warnOnce('NavigationDrawer', 'All links with groupType large must have a groups prop with at least one child link.');
            }
            let addTopSpacing = false;
            if (link.groupIndex !== undefined && link.groupIndex !== groupIndex) {
                // add spacer
                groupIndex = link.groupIndex;
                addTopSpacing = true;
            }
            const dataProps = {
                ...link,
                // @ts-ignore
                links: link.groups && flattenGroupsIntoLinks(link.groups),
            };
            return (React.createElement(NavigationDrawerLink, { key: (link === null || link === void 0 ? void 0 : link.path) + `${position}` || (link === null || link === void 0 ? void 0 : link.label) + `${position}` || position, ...dataProps, addTopSpacing: addTopSpacing, setColumn: setColumn, index: index, active: link.path ? activePaths.indexOf(link.path) > -1 : false, ...createFocusProps(position) }));
        }))));
});
NavigationDrawerList.displayName = 'NavigationDrawerList';
export default NavigationDrawerList;
const NavigationWrapper = styled.nav `
  flex: 1;

  ${responsiveStyles({
    marginBlockStart: {
        mobile: Spacings.none,
        desktopWide: Spacings.none,
    },
    marginBlockEnd: {
        mobile: Spacings.large,
        tablet: Spacings.large,
        desktop: Spacings.xLarge,
        desktopWide: Spacings.xLarge,
    },
})}
`;
const NavigationDrawerLink = memo(forwardRef(({ label = '', eventLabel, index = 0, path, setColumn, onClick, addTopSpacing, active = false, ...props }, ref) => {
    var _a, _b;
    const { isKeyboardControlled } = useInteractionMode();
    const { handleNavigationDrawerTrackingData } = useTrackingData(DataLayerCategory.MENU);
    const handleSetColumn = useCallback(() => {
        setColumn(index + 1, { ...props, path });
    }, [setColumn, index, props, path]);
    const handleClick = useCallback(
    // TODO: does this fail on mobile? safari touch only
    (e) => {
        var _a;
        if (props.groups) {
            preventCancelableDefault(e);
            handleSetColumn();
        }
        // switch between opening up a grouped category or a link
        if (((_a = e.currentTarget) === null || _a === void 0 ? void 0 : _a.getAttribute('href')) !== '') {
            handleNavigationDrawerTrackingData(formatTrackingLabel(eventLabel), DataLayerAction.LINK_CLICK);
        }
        else {
            handleNavigationDrawerTrackingData(formatTrackingLabel(eventLabel), DataLayerAction.CATEGORY_CLICK);
        }
        if (onClick) {
            onClick(e);
        }
    }, [handleSetColumn, onClick, props.groups, handleNavigationDrawerTrackingData, eventLabel]);
    const hasLinks = !!props.groups && ((_a = props.groups) === null || _a === void 0 ? void 0 : _a.length) > 0;
    (_b = props.groups) === null || _b === void 0 ? void 0 : _b.forEach((group) => {
        if (!group.items || group.items.length === 0) {
            warnOnce('NavigationDrawerList', 'Groups need at least one item.');
        }
    });
    return (React.createElement(StyledLi, { ...aria({ role: 'presentation' }), addTopSpacing: addTopSpacing },
        React.createElement(StyledLink, { ref: ref, ...props, role: "menuitem", "aria-expanded": hasLinks ? active : undefined, href: props.href || '', active: active, onClick: handleClick, isKeyboardControlled: isKeyboardControlled, variant: LinkVariants.navigation },
            React.createElement("span", null, label),
            hasLinks && React.createElement(StyledIcon, { icon: Icons.chevronRight, size: Spacings.medium }))));
}));
NavigationDrawerLink.displayName = 'NavigationDrawerLink';
const StyledLi = styled.li(({ addTopSpacing }) => css(addTopSpacing &&
    css `
        ${responsiveStyles({
        marginBlockStart: {
            mobile: Spacings.large,
            desktop: Spacings.xLarge,
        },
    })}
      `));
const StyledLink = styled(Link)(({ theme, isKeyboardControlled, active, groupType }) => {
    return css(css `
      text-decoration: none;
      display: flex;
      cursor: pointer;
      align-items: center;
      justify-content: space-between;
      height: auto;
      /* prevent accidentally triggering hover state (messes with current focus state). */
      pointer-events: ${isKeyboardControlled ? 'none' : 'auto'};
    `, groupType === NavigationDrawerGroupTypes.large &&
        css `
        > span {
          transition: ${createCSSTransition('color', MotionEasings.easeOut, MotionDurations.normal)};
          color: ${active && theme.navigationDrawer.activeLinkColor};
        }

        /* font styling */
        ${responsiveStyles({
            fontSize: {
                mobile: FontSizes.large,
            },
            lineHeight: {
                mobile: LineHeights.large,
            },
            letterSpacing: {
                mobile: LetterSpacings.large,
            },
        })}
      `, groupType === NavigationDrawerGroupTypes.large &&
        css `
        @media (pointer: coarse) {
          padding: ${createCustomSpacing(6 / 16)} 0;
        }
        padding: ${createCustomSpacing(3 / 16)} 0;
      `);
});
const StyledIcon = styled(Icon) `
  text-align: center;
  width: 24px;
  display: flex;

  ${responsiveStyles({
    justifyContent: {
        mobile: 'flex-start',
        desktop: 'flex-end',
    },
})}
`;
